import axios from 'axios';
// Import the Supabase client
import { supabase } from './supabase'; // Assuming supabase.js exports the client

// Local `npm start` talks to the local backend. Production still uses Render
// unless REACT_APP_API_URL is set. Hitting Render from localhost is what made
// Learning At Markenzo show FastAPI's "Not Found" — that server has no academy
// routes yet.
const isLocalHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (isLocalHost ? 'http://localhost:8000' : 'https://markenzoess.onrender.com');
const API_URL = `${API_BASE_URL}/api`; // Ensure /api is included



// REMOVE Token management functions - Supabase handles this
// const getToken = () => localStorage.getItem('authToken');
// const setToken = (token) => localStorage.setItem('authToken', token);
// const removeToken = () => localStorage.removeItem('authToken');
// const getUserInfo = () => JSON.parse(localStorage.getItem('userInfo') || 'null');
// const setUserInfo = (userInfo) => localStorage.setItem('userInfo', JSON.stringify(userInfo));
// const removeUserInfo = () => localStorage.removeItem('userInfo');

// Create an axios instance
const axiosInstance = axios.create({
  baseURL: API_URL, // Use the corrected base URL
  headers: {
    'Content-Type': 'application/json',
  },
});

let cachedAccessToken = '';
let cachedTokenExp = 0;

const tokenExpiry = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(payload));
    return Number(claims.exp) || 0;
  } catch (error) {
    return 0;
  }
};

const currentAccessToken = async () => {
  const now = Date.now() / 1000;
  if (cachedAccessToken && cachedTokenExp > now + 30) {
    return cachedAccessToken;
  }
  const { data: { session } } = await supabase.auth.getSession();
  cachedAccessToken = session?.access_token || '';
  cachedTokenExp = tokenExpiry(cachedAccessToken);
  return cachedAccessToken;
};

// Request interceptor to add the Supabase token
axiosInstance.interceptors.request.use(
  async (config) => { // Make interceptor async
    const token = await currentAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    
    return Promise.reject(error);
  }
);

// Response interceptor for error handling (keep generic error logging)
axiosInstance.interceptors.response.use(
  (response) => {
     // Log successful response status
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
        cachedAccessToken = '';
        cachedTokenExp = 0;
        // Note: Supabase client automatically handles token refresh.
        // We don't need to manually clear tokens here unless specifically required.
        // Consider notifying the user or attempting a silent refresh if appropriate.
    } else {
        
    }
    return Promise.reject(error);
  }
);

// Authentication service using Supabase Auth
export const auth = {
  // Login using Supabase email/password
  login: async (credentials) => {
    
    const { email, password } = credentials; // Assuming credentials = {email, password}
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password,
    });

    if (error) {
      
      throw error; // Re-throw the error to be handled by the component
    }

    
    // Return the user and session data provided by Supabase
    return { user: data.user, session: data.session };
  },

  // Logout using Supabase
  logout: async () => {
    
    cachedAccessToken = '';
    cachedTokenExp = 0;
    const { error } = await supabase.auth.signOut();
    if (error) {
      
      throw error;
    }
    
    // No need to manually clear tokens, Supabase client handles it.
  },

  // Get current Supabase session (includes user info)
  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
        
        return null;
    }
    return session; // Contains user object if authenticated
  },

  // Listen for auth state changes (useful for updating UI)
  onAuthStateChange: (callback) => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session); // Pass event and session to the callback
    });
    return subscription; // Return the subscription object so it can be unsubscribed
  },

  // Signup using Supabase
  signup: async (credentials) => {
    const { email, password } = credentials;
    const { data, error } = await supabase.auth.signUp({
      email: email,
      password: password,
      // You can add options here, like redirect URLs or metadata
      // options: {
      //   emailRedirectTo: 'http://localhost:3000/chat',
      //   data: { full_name: 'Optional Name' } // Example metadata
      // }
    });

    if (error) {
      throw error; // Let the calling function (AuthContext) handle the error
    }

    // Existing emails can return a user with no identities and no error.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      throw new Error('An account with this email already exists. Try signing in, or use Forgot password.');
    }

    if (!data?.user) {
      throw new Error('Sign up failed. Please try again.');
    }

    // Signup successful. Data might contain user and session if auto-confirmed,
    // or just user if email confirmation is required.
    // The onAuthStateChange listener will typically handle the session update.
    return { user: data.user, session: data.session };
  },

  // Convenience function to get current user directly
  getCurrentUser: async () => {
      const session = await auth.getSession();
      return session?.user ?? null;
  },

  
  
  // Sign in with Google
  signInWithGoogle: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/home', // Or your desired redirect path
      },
    });
    if (error) throw error;
    return { user: data?.user, session: data?.session, url: data?.url };
  },
  // Remove getToken, getUserInfo etc. as Supabase manages the session
};

// Chat API service
let sessionsCache = { at: 0, data: null, pending: null };
let courseCache = { at: 0, data: null, pending: null };

export const chatApi = {
  getSessions: async () => {
    const now = Date.now();
    if (sessionsCache.data && now - sessionsCache.at < 20000) {
      return sessionsCache.data;
    }
    if (sessionsCache.pending) {
      return sessionsCache.pending;
    }
    sessionsCache.pending = axiosInstance
      .get('/chat/sessions')
      .then((response) => {
        const rows = response.data.sessions || [];
        sessionsCache = { at: Date.now(), data: rows, pending: null };
        return rows;
      })
      .catch(() => {
        sessionsCache.pending = null;
        return sessionsCache.data || [];
      });
    return sessionsCache.pending;
  },

  getSessionMessages: async (sessionId) => {
    
    try {
      // Correct path: /chat/sessions/{sessionId}/messages
      const response = await axiosInstance.get(`/chat/sessions/${sessionId}/messages`);
      // The backend returns {"messages": [...]}, which is what ChatWindow expects.
      // Just return the whole data object.
      return response.data || { messages: [] }; // Return the data or a default structure
    } catch (error) {
      
      return { messages: [] }; // Return default structure on error
    }
  },

  createSession: async () => {
    
    try {
       // Correct path: /chat/sessions
      const response = await axiosInstance.post('/chat/sessions');
      sessionsCache = { at: 0, data: null, pending: null };
      return response.data; // The created session object
    } catch (error) {
      
      // Optionally create a local session as fallback? Be careful with this.
      // For now, just throw the error
        throw error;
    }
  },

  // Modified sendMessage to handle streaming responses
  sendMessage: async (sessionId, message, onChunkReceived) => {
    const payload = { content: message };
    const endpoint = `/chat/sessions/${sessionId}/messages`;
    
    try {
      // Get the token directly before making the fetch call
      const accessToken = await currentAccessToken();
      if (!accessToken) {
        throw new Error("User not authenticated");
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, { // Use fetch API
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Handle HTTP errors (like 404, 500 from the backend)
        const errorText = await response.text();
        throw new Error(`Request failed with status code ${response.status}: ${errorText}`);
      }
      
      // Check if the response body exists and is readable
      if (!response.body) {
        throw new Error("Response body is missing or not readable.");
      }

      // Process the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          onChunkReceived(chunk); // Call the callback with the new chunk
        }
      }
      
      // Indicate stream completion (optional)
      onChunkReceived(null); // Pass null or a specific signal
      sessionsCache = { at: 0, data: null, pending: null };
      
    } catch (error) {
      
      // Propagate the error so the UI can handle it
      throw error;
    }
  },

  deleteSession: async (sessionId) => {
    
    try {
      // Correct path: /chat/sessions/{sessionId}
      await axiosInstance.delete(`/chat/sessions/${sessionId}`);
      return { success: true };
    } catch (error) {
      
      return { success: false, error };
    }
  },
};

// Knowledge Base API service
export const knowledgeApi = {
  addDocument: async (documentData) => {
    
    try {
      // Correct path: /knowledge/documents or /knowledge/upload
      // Interceptor will add Authorization header if Supabase session exists
      const response = await axiosInstance.post('/knowledge/upload', documentData);
      return { success: true, data: response.data };
    } catch (error) {
      
      return { success: false, error };
    }
  },

  uploadFile: async (file, metadata = {}) => {
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify(metadata));
      try {
          // Correct path: /knowledge/upload-file
          const response = await axiosInstance.post('/knowledge/upload-file', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
          });
          return { success: true, data: response.data };
      } catch (error) {
          
          return { success: false, error };
      }
  },

  searchDocuments: async (query) => {
    
    try {
        // Correct path: /knowledge/search
      const response = await axiosInstance.get('/knowledge/search', { params: { query } });
      return { success: true, data: response.data };
    } catch (error) {
      
      return { success: false, error };
    }
  },

  updateDocument: async (documentId, documentData) => {
    try {
      const response = await axiosInstance.put(`/knowledge/documents/${documentId}`, documentData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  uploadPdf: async (file, { title, source, category, allowedEmails }, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('source', source);
    formData.append('category', category || 'general');
    formData.append('allowed_emails', JSON.stringify(allowedEmails || []));

    try {
      const response = await axiosInstance.post('/knowledge/upload-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  replacePdf: async (parentId, file, { title, source, category, allowedEmails }, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('source', source);
    formData.append('category', category || 'general');
    if (allowedEmails) {
      formData.append('allowed_emails', JSON.stringify(allowedEmails));
    }

    try {
      const response = await axiosInstance.put(
        `/knowledge/documents/${parentId}/pdf`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
          onUploadProgress: (event) => {
            if (onProgress && event.total) {
              onProgress(Math.round((event.loaded * 100) / event.total));
            }
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  updateDocumentMetadata: async (parentId, metadata) => {
    try {
      const response = await axiosInstance.put(
        `/knowledge/documents/${parentId}/metadata`,
        metadata
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  getDocumentFileUrl: async (parentId) => {
    try {
      const response = await axiosInstance.get(`/knowledge/documents/${parentId}/file-url`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  listUsers: async () => {
    try {
      const response = await axiosInstance.get('/knowledge/users');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  getAccessOverview: async () => {
    try {
      const response = await axiosInstance.get('/knowledge/access');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  setDocumentAccess: async (documentKey, allowedEmails) => {
    try {
      const response = await axiosInstance.put(
        `/knowledge/documents/${documentKey}/access`,
        { allowed_emails: allowedEmails || [] }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  getSharedWithMe: async () => {
    try {
      const response = await axiosInstance.get('/knowledge/shared-with-me');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  getDocumentContent: async (documentKey) => {
    try {
      const response = await axiosInstance.get(`/knowledge/documents/${documentKey}/content`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },
};

// The academy surfaces server messages directly to the learner ("2 chapters
// still need to be read", "the correct answer must match an option"), so the
// detail is pulled out rather than handing back a raw axios error.
// FastAPI answers an unknown route with the detail "Not Found", which on its own
// tells the reader nothing, so prefer the caller's fallback over the generic ones.
const GENERIC_DETAILS = ['not found', 'internal server error'];

const academyError = (error, fallback) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const useDetail =
    typeof detail === 'string' && !GENERIC_DETAILS.includes(detail.trim().toLowerCase());

  let message = useDetail ? detail : fallback || error?.message;
  if (!error?.response) {
    message =
      'Could not reach the backend. Start it with `python -m uvicorn main:app --port 8000` from the backend folder.';
  } else if (status === 404) {
    message =
      'The academy API is not on this backend. Restart the local server from the backend folder so it picks up the new routes.';
  }

  return {
    success: false,
    error,
    message,
  };
};

export const suggestionApi = {
  submit: async (payload) => {
    try {
      const response = await axiosInstance.post('/suggestions/submit', payload);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not send your suggestion.');
    }
  },

  list: async () => {
    try {
      const response = await axiosInstance.get('/suggestions/inbox');
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load suggestions.');
    }
  },

  update: async (suggestionId, payload) => {
    try {
      const response = await axiosInstance.patch(`/suggestions/${suggestionId}`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not update the suggestion.');
    }
  },
};

export const recognitionApi = {
  feed: async () => {
    try {
      const response = await axiosInstance.get('/recognition/feed');
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load rewards.');
    }
  },

  nominate: async (payload) => {
    try {
      const response = await axiosInstance.post('/recognition/nominate', payload);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not send the nomination.');
    }
  },

  markSeen: async (awardId) => {
    try {
      const response = await axiosInstance.patch(`/recognition/${awardId}/seen`);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not open this award.');
    }
  },
};

export const academyApi = {
  getCourse: async () => {
    const now = Date.now();
    if (courseCache.data && now - courseCache.at < 15000) {
      return courseCache.data;
    }
    if (courseCache.pending) {
      return courseCache.pending;
    }
    courseCache.pending = axiosInstance
      .get('/academy/course')
      .then((response) => {
        const result = { success: true, data: response.data };
        courseCache = { at: Date.now(), data: result, pending: null };
        return result;
      })
      .catch((error) => {
        courseCache.pending = null;
        return academyError(error, 'Could not load the course.');
      });
    return courseCache.pending;
  },

  getChapter: async (chapterId) => {
    try {
      const response = await axiosInstance.get(`/academy/chapters/${chapterId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load this chapter.');
    }
  },

  markChapterRead: async (chapterId) => {
    try {
      const response = await axiosInstance.post(`/academy/chapters/${chapterId}/read`);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not save your progress.');
    }
  },

  getAssessment: async (sectionId) => {
    try {
      const response = await axiosInstance.get(`/academy/sections/${sectionId}/assessment`);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load the assessment.');
    }
  },

  submitAttempt: async (sectionId, answers) => {
    try {
      const response = await axiosInstance.post(
        `/academy/sections/${sectionId}/attempts`,
        { answers: answers || {} }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not submit your assessment.');
    }
  },

  getMyResults: async () => {
    try {
      const response = await axiosInstance.get('/academy/my-results');
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load your results.');
    }
  },

  getTeamProgress: async () => {
    try {
      const response = await axiosInstance.get('/academy/team-progress');
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load team progress.');
    }
  },

  createSection: async (payload) => {
    try {
      const response = await axiosInstance.post('/academy/sections', payload);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not create the section.');
    }
  },

  updateSection: async (sectionId, payload) => {
    try {
      const response = await axiosInstance.put(`/academy/sections/${sectionId}`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not save the section.');
    }
  },

  deleteSection: async (sectionId) => {
    try {
      const response = await axiosInstance.delete(`/academy/sections/${sectionId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not delete the section.');
    }
  },

  createChapter: async (sectionId, payload) => {
    try {
      const response = await axiosInstance.post(
        `/academy/sections/${sectionId}/chapters`,
        payload
      );
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not create the chapter.');
    }
  },

  updateChapter: async (chapterId, payload) => {
    try {
      const response = await axiosInstance.put(`/academy/chapters/${chapterId}`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not save the chapter.');
    }
  },

  deleteChapter: async (chapterId) => {
    try {
      const response = await axiosInstance.delete(`/academy/chapters/${chapterId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not delete the chapter.');
    }
  },

  getQuestions: async (sectionId) => {
    try {
      const response = await axiosInstance.get(`/academy/sections/${sectionId}/questions`);
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load the questions.');
    }
  },

  saveQuestions: async (sectionId, questions) => {
    try {
      const response = await axiosInstance.put(
        `/academy/sections/${sectionId}/questions`,
        { questions: questions || [] }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not save the questions.');
    }
  },

  seedContent: async () => {
    try {
      const response = await axiosInstance.post('/academy/seed');
      return { success: true, data: response.data };
    } catch (error) {
      return academyError(error, 'Could not load the course content.');
    }
  },
};

// Combine services (optional, based on usage)
const apiService = {
  auth,
  chat: chatApi,
  knowledge: knowledgeApi,
  academy: academyApi,
  suggestions: suggestionApi,
  recognition: recognitionApi,
  // Add other services if needed
};

export default apiService; // Export combined or individual services as needed

// Also export the configured axios instance directly
export { axiosInstance };